import React, { useState, useMemo } from 'react';
import { Calendar, Clock, Loader2, Lock, Truck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useCompany } from '../../context/CompanyContext';
import { getDisplayName } from '../../lib/utils';
import { fetchServiceDefaults, resolveDefaultMode } from '../../utils/serviceDefaults';
import { NON_BILLABLE_REASONS, requiresNote } from '../../lib/billingIntent';
import { Ban } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Full 24-hour time slots (30-min intervals) to match the calendar grid
const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const totalMinutes = i * 30;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});

const DURATIONS = [
  { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours', value: 120 },
  { label: '2.5 hours', value: 150 },
  { label: '3 hours', value: 180 },
  { label: '4 hours', value: 240 },
];

const formatTimeLabel = (t) => {
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${hour}:${m === 0 ? '00' : '30'} ${period}`;
};

const getTomorrowDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

/**
 * ScheduleJobModal
 *
 * Allows dispatchers to schedule a job for a specific lead.
 * Reuses the same addTask / updateLead logic as the drag-and-drop scheduler.
 *
 * Props:
 *   lead          — the lead/client document to schedule
 *   isOpen        — boolean
 *   onClose       — fn()
 *   quoteId       — (optional) if provided and deposit is enabled, auto-creates deposit invoice
 *   forceOneTime  — (optional, default false) explicit one-time intent. When true:
 *                     • the modal ALWAYS creates a NEW task with isRecurring=false,
 *                       bypassing the "reschedule existing job" code path. Used by
 *                       OneTimeJobsSection so it never accidentally rebinds an
 *                       inactive recurring visit (iter-268 RCA).
 */
const ScheduleJobModal = ({ lead, isOpen, onClose, quoteId, initialDate, initialStartTime, initialDuration, initialJobMode, forceOneTime = false }) => {
  const { addTask, updateTask, tasks } = useTasks();
  const { leads, updateLead } = useLeads();
  const { companyId } = useCompany();

  const [date, setDate] = useState(getTomorrowDate);
  const [startTime, setStartTime] = useState('09:00');
  const [duration, setDuration] = useState(60);
  const [submitting, setSubmitting] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  // Two-mode scheduling: defaults to 'appointment' for new schedules (the safer
  // of the two — over-commits are recoverable, under-commits leave a customer
  // expecting a real visit without one). Pre-empted by `initialJobMode` prop
  // or by the company's per-service-category default (resolved below).
  const [jobMode, setJobMode] = useState(initialJobMode || 'appointment');

  // Billing Intent — optional, unobtrusive No Charge. Default path stays
  // zero-click billable; the control is collapsed until the operator opts in.
  const [noCharge, setNoCharge] = useState(false);
  const [ncReason, setNcReason] = useState('warranty');
  const [ncNote, setNcNote] = useState('');

  // Pre-fill form when editing an existing scheduled job
  React.useEffect(() => {
    if (isOpen) {
      setDate(initialDate || getTomorrowDate());
      setStartTime(initialStartTime || '09:00');
      setDuration(initialDuration || 60);
      setSelectedLeadId('');
      setNoCharge(false);
      setNcReason('warranty');
      setNcNote('');
      if (initialJobMode === 'appointment' || initialJobMode === 'route_flexible') {
        setJobMode(initialJobMode);
      }
    }
  }, [isOpen, initialDate, initialStartTime, initialDuration, initialJobMode]);

  // When opened from a slot click (no lead prop), compute unscheduled leads to pick from
  const unscheduledLeads = useMemo(() => {
    if (lead || !leads || !tasks) return [];
    const approvedLeads = leads.filter(l => l.stage === 'Approved');
    const scheduledLeadIds = new Set(
      tasks.filter(t => t.type === 'job' && t.scheduledStart).map(t => t.leadId)
    );
    return approvedLeads.filter(l => !scheduledLeadIds.has(l.id));
  }, [lead, leads, tasks]);

  // The lead to actually schedule — either passed in (edit/quote flow) or chosen from picker
  const resolvedLead = lead || leads?.find(l => l.id === selectedLeadId) || null;

  // Resolve the category default for the resolved lead unless the caller
  // explicitly passed initialJobMode. Pure read — never overrides a user
  // who has already clicked the toggle (we only update when modal opens
  // and lead identity changes).
  React.useEffect(() => {
    if (!isOpen || !companyId) return;
    if (initialJobMode === 'appointment' || initialJobMode === 'route_flexible') return;
    if (!resolvedLead?.service) return;
    let cancelled = false;
    (async () => {
      const defaults = await fetchServiceDefaults(companyId);
      if (cancelled) return;
      const mode = resolveDefaultMode(defaults, resolvedLead.service, 'appointment');
      setJobMode(mode);
    })();
    return () => { cancelled = true; };
  }, [isOpen, companyId, resolvedLead?.id, resolvedLead?.service, initialJobMode]);

  const handleSchedule = async () => {
    // Route-flexible mode only requires date; appointment requires both.
    if (!resolvedLead || !date) return;
    if (jobMode === 'appointment' && !startTime) return;
    if (noCharge && requiresNote(ncReason) && !ncNote.trim()) {
      toast.error('Add a short note for "Other".');
      return;
    }
    setSubmitting(true);
    try {
      const [year, month, day] = date.split('-').map(Number);
      const isRoute = jobMode === 'route_flexible';
      const [hour, minute] = isRoute ? [0, 0] : startTime.split(':').map(Number);
      const start = new Date(year, month - 1, day, hour, minute, 0, 0);
      const end = isRoute ? null : new Date(start.getTime() + duration * 60 * 1000);

      const leadId = resolvedLead.id;
      // forceOneTime: explicit caller intent (OneTimeJobsSection). Skip the
      // "reschedule existing job" path entirely — that path can rebind a
      // historic recurring task and silently produce a recurring visit.
      const existingJob = forceOneTime
        ? null
        : tasks.find((t) => t.leadId === leadId && t.type === 'job');
      const scheduleFields = {
        scheduledStart: start.toISOString(),
        scheduledEnd:   end ? end.toISOString() : null,
        status:         'scheduled',
        jobMode,
        hasTime:        !isRoute,
        serviceDate:    `${date}`,
      };

      if (existingJob) {
        await updateTask(existingJob.id, scheduleFields);
      } else {
        await addTask({
          leadId,
          title: getDisplayName(resolvedLead, '') || resolvedLead.service || 'Job',
          clientName: getDisplayName(resolvedLead, '') || null,
          serviceType: resolvedLead.service || null,
          type: 'job',
          isRecurring: false,
          // Billing Intent — default billable; No Charge only when opted in.
          ...(noCharge
            ? {
                billingIntent: 'non_billable',
                nonBillableReason: ncReason,
                ...(ncNote.trim() ? { nonBillableNote: ncNote.trim() } : {}),
              }
            : {}),
          // Phase 1 lineage: only when this modal was opened from a quote.
          ...(quoteId ? { quoteId } : {}),
          ...scheduleFields,
        });
      }

      // Advance lead stage to 'Booked' — same as DnD drop behaviour
      await updateLead(leadId, { stage: 'Booked' });

      toast.success(`Job scheduled for ${getDisplayName(resolvedLead, 'Customer')}`);

      // ── Auto-create deposit invoice if quoteId supplied (best-effort) ─────
      if (quoteId && companyId) {
        try {
          const { getAuth } = await import('firebase/auth');
          const token = await getAuth().currentUser?.getIdToken();
          if (token) {
            const res = await axios.post(`${API}/api/invoices/create-deposit`, {
              companyId,
              quoteId,
              originUrl: window.location.origin,
              sendSms: true,
            }, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data?.success && !res.data?.alreadyExists) {
              toast.success('Deposit invoice created and payment link sent via SMS');
            }
          }
        } catch (depErr) {
          // Deposit errors are non-blocking (user can create manually)
          const msg = depErr.response?.data?.detail || '';
          if (msg && !msg.includes('not enabled') && !msg.includes('$0')) {
            toast.error(`Deposit invoice: ${msg}`);
          }
        }
      }

      onClose();
    } catch (err) {
      console.error('[ScheduleJobModal] Error scheduling job:', err);
      toast.error('Failed to schedule job');
    } finally {
      setSubmitting(false);
    }
  };

  const displayName = resolvedLead ? getDisplayName(resolvedLead, 'Customer') : '';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-orange-500" />
            Schedule Job
          </DialogTitle>
        </DialogHeader>

        {lead && (
          <div className="text-sm text-gray-600 -mt-1">
            <span className="font-semibold text-gray-900">{displayName}</span>
            {lead.service && (
              <span className="ml-1 text-gray-500">· {lead.service}</span>
            )}
          </div>
        )}

        {/* Lead picker — only in slot-click mode (no lead pre-selected) */}
        {!lead && (
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Job
            </label>
            <select
              value={selectedLeadId}
              onChange={(e) => setSelectedLeadId(e.target.value)}
              data-testid="schedule-job-lead-select"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            >
              <option value="">— select a job to schedule —</option>
              {unscheduledLeads.map((l) => (
                <option key={l.id} value={l.id}>
                  {getDisplayName(l, 'Job')}{l.service ? ` · ${l.service}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <Separator />

        <div className="space-y-4">
          {/* Two-mode toggle — appointment commits a customer time;
              route-flexible is a date-only stop planned by Build Day. */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              How is this job scheduled?
            </label>
            <div className="flex gap-2" role="tablist">
              <button
                type="button"
                data-testid="schedule-job-mode-appointment"
                onClick={() => setJobMode('appointment')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition ${
                  jobMode === 'appointment'
                    ? 'bg-orange-50 border-orange-400 text-orange-700 font-semibold'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Lock className="h-3.5 w-3.5" />
                Fixed Appointment
              </button>
              <button
                type="button"
                data-testid="schedule-job-mode-route"
                onClick={() => setJobMode('route_flexible')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition ${
                  jobMode === 'route_flexible'
                    ? 'bg-orange-50 border-orange-400 text-orange-700 font-semibold'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Truck className="h-3.5 w-3.5" />
                Route Stop
              </button>
            </div>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              {jobMode === 'appointment'
                ? 'Customer receives a specific arrival time.'
                : 'Customer receives a service date only. Dispatch and Build Day determine the route sequence and ETA later.'}
            </p>
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              data-testid="schedule-job-date"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            />
          </div>

          {/* Start Time — hidden for route-flexible. */}
          {jobMode === 'appointment' && (
          <div className="space-y-1.5" data-testid="schedule-job-time-block">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Start Time
            </label>
            <select
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              data-testid="schedule-job-time"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            >
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t}>{formatTimeLabel(t)}</option>
              ))}
            </select>
          </div>
          )}

          {/* Duration */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Duration
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              data-testid="schedule-job-duration"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            >
              {DURATIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          {/* Billing Intent — optional No Charge (collapsed by default) */}
          <div className="space-y-1.5">
            <button
              type="button"
              onClick={() => setNoCharge((v) => !v)}
              data-testid="schedule-job-no-charge-toggle"
              className={`flex items-center gap-1.5 text-xs font-medium transition ${
                noCharge ? 'text-slate-800' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Ban className="h-3.5 w-3.5" />
              {noCharge ? 'No Charge (no invoice)' : 'Mark as No Charge'}
            </button>
            {noCharge && (
              <div className="space-y-2 pt-1">
                <select
                  value={ncReason}
                  onChange={(e) => setNcReason(e.target.value)}
                  data-testid="schedule-job-no-charge-reason"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {NON_BILLABLE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {requiresNote(ncReason) && (
                  <input
                    type="text"
                    value={ncNote}
                    onChange={(e) => setNcNote(e.target.value)}
                    placeholder="Add a short note"
                    data-testid="schedule-job-no-charge-note"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                      focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSchedule}
            disabled={submitting || !date || !resolvedLead || (jobMode === 'appointment' && !startTime)}
            data-testid="schedule-job-confirm-btn"
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scheduling...</>
            ) : (
              'Schedule Job'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleJobModal;
